import {
  convertHeightFromCm,
  convertHeightToCm,
  convertWaistFromCm,
  convertWaistToCm,
  validateHeight,
  validateWaist,
  validateWeight,
} from "../../utils/eira/helper";
import { HEIGHT_UNITS, WAIST_UNITS } from "../../utils/interfaces";

// Default min/max values for each vital type
export const VITAL_DEFAULTS = {
  height: {
    minValue: 80, // cm (~2'07")
    maxValue: 260, // cm (~8'6")
  },
  weight: {
    minValue: 1, // kg (~55 lbs)
    maxValue: 300, // kg (~496 lbs)
  },
  waist: {
    cm: {
      minValue: 35, // cm
      maxValue: 160, // cm
    },
    inches: {
      minValue: 13, // inches
      maxValue: 63, // inches
    },
  },
  bloodPressure: {
    minValue: 70, // systolic
    maxValue: 200, // systolic
  },
  temperature: {
    minValue: 35, // celsius
    maxValue: 45, // celsius
  },
};

// Height Configuration - ENHANCED WITH MIN/MAX SUPPORT
export const HEIGHT_CONFIG = {
  convertToBase: (value: string, fromUnit: string): number => {
    return convertHeightToCm(value, fromUnit);
  },
  convertFromBase: (baseValue: number, toUnit: string): string => {
    return convertHeightFromCm(baseValue, toUnit);
  },
  validateValue: (
    value: string,
    unit: string,
    minValue?: number,
    maxValue?: number
  ): string => {
    return validateHeight(value, unit, minValue, maxValue);
  },
  storageUnit: "base", // Store in cm (base unit)
  formatDisplay: (value: string, unit: string, isEditing: boolean): string => {
    if (!value || value.trim() === "" || isEditing) return value;

    if (unit === "ft_in") {
      // Parse the value properly
      const feet = Math.floor(parseFloat(value));

      // CRITICAL FIX: Extract inches from decimal part correctly
      const valueStr = value.toString();
      const decimalIndex = valueStr.indexOf(".");
      let inches = 0;

      if (decimalIndex !== -1) {
        const decimalDigits = valueStr.substring(decimalIndex + 1);
        inches = parseInt(decimalDigits) || 0;
      }

      // EDGE CASE FIX: Handle inches >= 12
      if (inches >= 12) {
        const additionalFeet = Math.floor(inches / 12);
        inches = inches % 12;
        return `${feet + additionalFeet}ft ${inches}inches`;
      }

      return `${feet}ft ${inches}inches`;
    } else if (unit === "cm") {
      return `${value} cm`;
    }
    return value;
  },
  parseDisplay: (displayValue: string): string => {
    if (!displayValue || displayValue.trim() === "") return "";

    // Handle feet/inches format: "5ft 1inches" -> "5.01", "5ft 10inches" -> "5.10"
    const feetInchesMatch = displayValue.match(/(\d+)ft\s*(\d+)inches?/);
    if (feetInchesMatch) {
      const feet = parseInt(feetInchesMatch[1]);
      let inches = parseInt(feetInchesMatch[2]);

      // EDGE CASE FIX: Handle inches >= 12 in parsing
      if (inches >= 12) {
        const additionalFeet = Math.floor(inches / 12);
        inches = inches % 12;
        const totalFeet = feet + additionalFeet;

        if (inches === 0) {
          return `${totalFeet}.00`;
        } else if (inches < 10) {
          return `${totalFeet}.0${inches}`;
        } else {
          return `${totalFeet}.${inches}`;
        }
      }

      // CRITICAL FIX: Format inches properly
      if (inches === 0) {
        return `${feet}.00`;
      } else if (inches < 10) {
        return `${feet}.0${inches}`;
      } else {
        return `${feet}.${inches}`;
      }
    }

    // Handle cm suffix: "175 cm" -> "175"
    return displayValue.replace(/\s*cm$/g, "").trim();
  },
};

// Weight Configuration - ENHANCED WITH MIN/MAX SUPPORT
export const WEIGHT_CONFIG = {
  convertToBase: (value: string, fromUnit: string): number => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 0;

    switch (fromUnit) {
      case "kg":
        return numValue;
      case "lbs":
        return numValue * 0.453592; // Convert lbs to kg
      default:
        return numValue;
    }
  },
  convertFromBase: (baseValue: number, toUnit: string): string => {
    if (isNaN(baseValue) || baseValue <= 0) return "";

    switch (toUnit) {
      case "kg":
        return Math.round(baseValue).toString();
      case "lbs":
        return Math.round(baseValue * 2.20462).toString(); // Convert kg to lbs
      default:
        return baseValue.toString();
    }
  },
  validateValue: (
    value: string,
    unit: string,
    minValue?: number,
    maxValue?: number
  ): string => {
    return validateWeight(value, unit, minValue, maxValue);
  },
  storageUnit: "base", // Store in kg (base unit)
  formatDisplay: (value: string, unit: string, isEditing: boolean): string => {
    if (!value || value.trim() === "" || isEditing) return value;

    if (unit === "kg") {
      return `${value} kg`;
    } else if (unit === "lbs") {
      return `${value} lbs`;
    }
    return value;
  },
};

// Waist Configuration - ENHANCED WITH MIN/MAX SUPPORT
export const WAIST_CONFIG = {
  convertToBase: (value: string, fromUnit: string): number => {
    return convertWaistToCm(value, fromUnit);
  },
  convertFromBase: (baseValue: number, toUnit: string): string => {
    return convertWaistFromCm(baseValue, toUnit);
  },
  validateValue: (
    value: string,
    unit: string,
    minValue?: number,
    maxValue?: number
  ): string => {
    return validateWaist(value, unit, minValue, maxValue);
  },
  storageUnit: "inches", // Store in inches (based on your current logic)
  storageConversion: (value: string, currentUnit: string): string => {
    if (!value || isNaN(parseFloat(value))) return "";

    if (currentUnit === "inches") {
      return value;
    } else {
      // Convert cm to inches for storage
      const cmValue = parseFloat(value);
      const inchesValue = Math.round(cmValue * 0.393701);
      return inchesValue.toString();
    }
  },
  formatDisplay: (value: string, unit: string, isEditing: boolean): string => {
    if (!value || value.trim() === "" || isEditing) return value;

    if (unit === "inches") {
      return `${value} in`;
    } else if (unit === "cm") {
      return `${value} cm`;
    }
    return value;
  },
};

// Blood Pressure Configuration (for future use) - ENHANCED WITH MIN/MAX SUPPORT
export const BLOOD_PRESSURE_CONFIG = {
  convertToBase: (value: string, fromUnit: string): number => {
    // Blood pressure doesn't need unit conversion typically
    return parseFloat(value) || 0;
  },
  convertFromBase: (baseValue: number, toUnit: string): string => {
    return baseValue.toString();
  },
  validateValue: (
    value: string,
    unit: string,
    minValue?: number,
    maxValue?: number
  ): string => {
    // Basic validation for blood pressure (systolic/diastolic)
    if (!value || value.trim() === "") return "";

    const parts = value.split("/");
    if (parts.length !== 2) {
      return "Blood pressure should be in format: systolic/diastolic (e.g., 120/80)";
    }

    const systolic = parseFloat(parts[0]);
    const diastolic = parseFloat(parts[1]);

    if (isNaN(systolic) || isNaN(diastolic)) {
      return "Invalid blood pressure values.";
    }

    // Use provided min/max or defaults
    const minSystolic = minValue || VITAL_DEFAULTS.bloodPressure.minValue;
    const maxSystolic = maxValue || VITAL_DEFAULTS.bloodPressure.maxValue;
    const minDiastolic = 40;
    const maxDiastolic = 120;

    if (
      systolic < minSystolic ||
      systolic > maxSystolic ||
      diastolic < minDiastolic ||
      diastolic > maxDiastolic
    ) {
      return "Blood pressure values seem unusual. Please check.";
    }

    return "";
  },
  storageUnit: "base",
};

// Temperature Configuration (for future use) - ENHANCED WITH MIN/MAX SUPPORT
export const TEMPERATURE_CONFIG = {
  convertToBase: (value: string, fromUnit: string): number => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 0;

    switch (fromUnit) {
      case "celsius":
        return numValue;
      case "fahrenheit":
        return ((numValue - 32) * 5) / 9; // Convert F to C
      default:
        return numValue;
    }
  },
  convertFromBase: (baseValue: number, toUnit: string): string => {
    if (isNaN(baseValue)) return "";

    switch (toUnit) {
      case "celsius":
        return baseValue.toFixed(1);
      case "fahrenheit":
        return ((baseValue * 9) / 5 + 32).toFixed(1); // Convert C to F
      default:
        return baseValue.toString();
    }
  },
  validateValue: (
    value: string,
    unit: string,
    minValue?: number,
    maxValue?: number
  ): string => {
    if (!value || value.trim() === "") return "";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "Invalid temperature.";

    // Convert to celsius for validation
    let celsiusValue = numValue;
    if (unit === "fahrenheit") {
      celsiusValue = ((numValue - 32) * 5) / 9;
    }

    // Use provided min/max or defaults (in celsius)
    const minC = minValue || VITAL_DEFAULTS.temperature.minValue;
    const maxC = maxValue || VITAL_DEFAULTS.temperature.maxValue;

    if (celsiusValue < minC || celsiusValue > maxC) {
      return "Temperature seems unusual. Please check.";
    }

    return "";
  },
  storageUnit: "base", // Store in celsius
  formatDisplay: (value: string, unit: string, isEditing: boolean): string => {
    if (!value || value.trim() === "" || isEditing) return value;

    if (unit === "celsius") {
      return `${value}°C`;
    } else if (unit === "fahrenheit") {
      return `${value}°F`;
    }
    return value;
  },
};

// Unit definitions for different vitals
export const WEIGHT_UNITS = [
  { value: "kg", label: "kg", conversionFactor: 1 },
  { value: "lbs", label: "lbs", conversionFactor: 2.20462 },
];

export const BLOOD_PRESSURE_UNITS = [
  { value: "mmHg", label: "mmHg", conversionFactor: 1 },
];

export const TEMPERATURE_UNITS = [
  { value: "celsius", label: "°C", conversionFactor: 1 },
  { value: "fahrenheit", label: "°F", conversionFactor: 1 },
];

// Export all configs for easy access
export const VITAL_CONFIGS = {
  height: HEIGHT_CONFIG,
  weight: WEIGHT_CONFIG,
  waist: WAIST_CONFIG,
  bloodPressure: BLOOD_PRESSURE_CONFIG,
  temperature: TEMPERATURE_CONFIG,
};

export const VITAL_UNITS = {
  height: HEIGHT_UNITS,
  weight: WEIGHT_UNITS,
  waist: WAIST_UNITS,
  bloodPressure: BLOOD_PRESSURE_UNITS,
  temperature: TEMPERATURE_UNITS,
};

// Helper function to get min/max values for a specific vital and unit
export const getMinMaxValues = (
  vitalType: keyof typeof VITAL_CONFIGS,
  unit?: string
) => {
  switch (vitalType) {
    case "height":
      return {
        minValue: VITAL_DEFAULTS.height.minValue,
        maxValue: VITAL_DEFAULTS.height.maxValue,
      };
    case "weight":
      return {
        minValue: VITAL_DEFAULTS.weight.minValue,
        maxValue: VITAL_DEFAULTS.weight.maxValue,
      };
    case "waist":
      if (unit === "cm") {
        return {
          minValue: VITAL_DEFAULTS.waist.cm.minValue,
          maxValue: VITAL_DEFAULTS.waist.cm.maxValue,
        };
      } else {
        return {
          minValue: VITAL_DEFAULTS.waist.inches.minValue,
          maxValue: VITAL_DEFAULTS.waist.inches.maxValue,
        };
      }
    case "bloodPressure":
      return {
        minValue: VITAL_DEFAULTS.bloodPressure.minValue,
        maxValue: VITAL_DEFAULTS.bloodPressure.maxValue,
      };
    case "temperature":
      return {
        minValue: VITAL_DEFAULTS.temperature.minValue,
        maxValue: VITAL_DEFAULTS.temperature.maxValue,
      };
    default:
      return { minValue: undefined, maxValue: undefined };
  }
};
